import React, { useState } from 'react';
import { Card, Table, Button, Space, Input, Select, DatePicker, Tag, Modal, Descriptions, message } from 'antd';
import { SearchOutlined, ReloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatIndianNumber, formatIndianCurrency } from '../../../utils/numberFormat';
import { reportsAPI } from '../../../services/api';

const ReportsDLR = () => {
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [mobileNumber, setMobileNumber] = useState('');
  const [headerFilter, setHeaderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchData = async (p = 1, ps = 10) => {
    if (!fromDate || !toDate) {
      message.warning('Please select From Date and To Date');
      return;
    }
    setLoading(true);
    try {
      const params = { page: p, page_size: ps, report_type: 'dlr', start_date: fromDate.format('YYYY-MM-DD'), end_date: toDate.format('YYYY-MM-DD') };
      if (mobileNumber) params.mobile_number = mobileNumber;
      if (headerFilter) params.header = headerFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await reportsAPI.getSMSDLR(params);
      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
        setTotal(response.data.total || 0);
      } else {
        setData([]);
        setTotal(0);
      }
      setPage(p);
      setPageSize(ps);
    } catch (err) {
      message.error('Failed to fetch DLR reports');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'delivered' || s === 'delivrd') return <Tag color="success">{status}</Tag>;
    if (s === 'failed' || s === 'rejected' || s === 'rejectd') return <Tag color="error">{status}</Tag>;
    if (s === 'pending') return <Tag color="warning">{status}</Tag>;
    if (s === 'expired') return <Tag color="orange">{status}</Tag>;
    return <Tag>{status}</Tag>;
  };

  const columns = [
    { title: 'User', dataIndex: 'username', key: 'username', width: 100, render: (v, r) => v || `User ${r.user_id}` },
    { title: 'Mobile', dataIndex: 'mobile_number', key: 'mobile', width: 120 },
    { title: 'Message', dataIndex: 'message_content', key: 'message', ellipsis: true, width: 200,
      render: (v, r) => (
        <span style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => { setSelectedMsg(r); setModalVisible(true); }}>
          {v ? (v.length > 40 ? v.substring(0, 40) + '...' : v) : '-'}
        </span>
      )
    },
    { title: 'DLR Status', dataIndex: 'dlr_status', key: 'dlr_status', width: 100, render: (v, r) => getStatusTag(v || r.status) },
    { title: 'DLR Time', dataIndex: 'dlr_time', key: 'dlr_time', width: 150, render: (v) => v ? dayjs(v).format('DD-MM-YYYY HH:mm:ss') : '-' },
    { title: 'Submitted', dataIndex: 'submitted_count', key: 'submitted', width: 80, align: 'right', render: (v) => <span style={{ color: '#52c41a' }}>{formatIndianNumber(v || 0)}</span> },
    { title: 'Delivered', dataIndex: 'delivered_count', key: 'delivered', width: 80, align: 'right', render: (v) => <span style={{ color: '#52c41a' }}>{formatIndianNumber(v || 0)}</span> },
    { title: 'Failed', dataIndex: 'failed_count', key: 'failed', width: 70, align: 'right', render: (v) => <span style={{ color: v > 0 ? '#ff4d4f' : 'inherit' }}>{formatIndianNumber(v || 0)}</span> },
    { title: 'DLR %', dataIndex: 'dlr_percentage', key: 'dlr_pct', width: 70, align: 'right', render: (v) => `${v || 0}%` },
    { title: 'Cost', dataIndex: 'cost', key: 'cost', width: 90, align: 'right', render: (v) => formatIndianCurrency(v || 0) },
    { title: 'Date', dataIndex: 'created_at', key: 'date', width: 150, render: (v) => v ? dayjs(v).format('DD-MM-YYYY HH:mm:ss') : '-' },
  ];

  return (
    <div style={{ padding: '16px' }}>
      <Card
        title={<span><CheckCircleOutlined style={{ marginRight: 8 }} />DLR Status Report</span>}
        bodyStyle={{ padding: '16px' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end' }}>
            <DatePicker value={fromDate} onChange={setFromDate} format="DD-MM-YYYY" placeholder="From Date" style={{ minWidth: 130 }} />
            <DatePicker value={toDate} onChange={setToDate} format="DD-MM-YYYY" placeholder="To Date" style={{ minWidth: 130 }} />
            <Input placeholder="Mobile" value={mobileNumber} onChange={e => setMobileNumber(e.target.value)} style={{ width: 140, flex: 'none' }} allowClear />
            <Input placeholder="Header" value={headerFilter} onChange={e => setHeaderFilter(e.target.value)} style={{ width: 130, flex: 'none' }} allowClear />
            <Select placeholder="Status" value={statusFilter || undefined} onChange={v => setStatusFilter(v || '')} style={{ minWidth: 120 }} allowClear>
              <Select.Option value="delivered">Delivered</Select.Option>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
              <Select.Option value="expired">Expired</Select.Option>
            </Select>
            <Button type="primary" icon={<SearchOutlined />} onClick={() => fetchData(1, pageSize)} loading={loading}>Search</Button>
            <Button icon={<ReloadOutlined />} onClick={() => fetchData(page, pageSize)}>Refresh</Button>
          </div>

          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table
              columns={columns}
              dataSource={data}
              rowKey="id"
              loading={loading}
              size="small"
              scroll={{ x: 1300 }}
              pagination={{
                current: page,
                pageSize: pageSize,
                total: total,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                showTotal: (t, range) => `${range[0]}-${range[1]} of ${formatIndianNumber(t)} records`,
                onChange: (p, s) => fetchData(p, s),
                size: 'small',
              }}
            />
          </div>
        </Space>
      </Card>

      <Modal
        title="DLR Message Details"
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setSelectedMsg(null); }}
        footer={<Button onClick={() => { setModalVisible(false); setSelectedMsg(null); }}>Close</Button>}
        width={640}
      >
        {selectedMsg && (
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="User">{selectedMsg.username || `User ${selectedMsg.user_id}`}</Descriptions.Item>
            <Descriptions.Item label="Mobile">{selectedMsg.mobile_number || '—'}</Descriptions.Item>
            <Descriptions.Item label="DLR Status">{getStatusTag(selectedMsg.dlr_status || selectedMsg.status)}</Descriptions.Item>
            <Descriptions.Item label="DLR Time">{selectedMsg.dlr_time ? dayjs(selectedMsg.dlr_time).format('DD-MM-YYYY HH:mm:ss') : '—'}</Descriptions.Item>
            <Descriptions.Item label="Submitted">{selectedMsg.submitted_count || 0}</Descriptions.Item>
            <Descriptions.Item label="Delivered">{selectedMsg.delivered_count || 0}</Descriptions.Item>
            <Descriptions.Item label="Failed">{selectedMsg.failed_count || 0}</Descriptions.Item>
            <Descriptions.Item label="DLR %">{selectedMsg.dlr_percentage || 0}%</Descriptions.Item>
            <Descriptions.Item label="Cost">{formatIndianCurrency(selectedMsg.cost || 0)}</Descriptions.Item>
            <Descriptions.Item label="Date">{selectedMsg.created_at ? dayjs(selectedMsg.created_at).format('DD-MM-YYYY HH:mm:ss') : '—'}</Descriptions.Item>
            <Descriptions.Item label="Message" span={2}>
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12, maxHeight: 200, overflow: 'auto' }}>
                {selectedMsg.message_content || '—'}
              </div>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default ReportsDLR;
